import { Fragment, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlayIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import {
    apiClient,
    GlobalTaskMemberResponse,
    GlobalTaskRunResponse,
    GlobalTaskTenantRunResponse,
} from '../lib/api';
import ConfirmationModal from '../components/ConfirmationModal';
import RunLarsStagesModal from '../components/RunLarsStagesModal';

function formatScheduleName(name: string) {
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusBadge(status: string | null | undefined) {
    const colors: Record<string, string> = {
        success: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
        running: 'bg-blue-100 text-blue-800',
        pending: 'bg-gray-100 text-gray-800',
        skipped: 'bg-yellow-100 text-yellow-800',
    };
    const key = status || 'unknown';
    return (
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[key] || 'bg-gray-100 text-gray-800'}`}>
            {key}
        </span>
    );
}

type PendingRun =
    | { kind: 'all'; force: boolean }
    | { kind: 'tenant'; tenantId: string };

export default function GlobalTaskDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [runningAll, setRunningAll] = useState(false);
    const [runningTenantId, setRunningTenantId] = useState<string | null>(null);
    const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
    const [tenantRunsByRun, setTenantRunsByRun] = useState<Record<string, GlobalTaskTenantRunResponse[]>>({});
    const [pendingRun, setPendingRun] = useState<PendingRun | null>(null);
    const [stageModalOpen, setStageModalOpen] = useState(false);
    const [togglingTenantId, setTogglingTenantId] = useState<string | null>(null);

    const { data: task, isLoading: taskLoading } = useQuery({
        queryKey: ['global-task', id],
        queryFn: () => apiClient.getGlobalTask(id!),
        enabled: !!id,
    });

    const { data: members = [], isLoading: membersLoading } = useQuery({
        queryKey: ['global-task-members', id],
        queryFn: () => apiClient.getGlobalTaskMembers(id!),
        enabled: !!id,
    });

    const { data: runs = [], isLoading: runsLoading } = useQuery({
        queryKey: ['global-task-runs', id],
        queryFn: () => apiClient.getGlobalTaskRuns(id!),
        enabled: !!id,
    });

    const isLarsTask = task?.schedule_name === 'run_lars_stages';
    const defaultStages = useMemo(() => {
        const stages = task?.task_extra_data?.stages;
        if (Array.isArray(stages) && stages.length) {
            return stages.map((s) => Number(s)).filter((s) => s >= 1 && s <= 4);
        }
        return [1, 2, 3, 4];
    }, [task?.task_extra_data?.stages]);

    const handleToggleMember = async (member: GlobalTaskMemberResponse) => {
        if (togglingTenantId) return;
        setTogglingTenantId(member.tenant_id);
        setError(null);
        try {
            await apiClient.updateGlobalTaskMember(id!, member.tenant_id, !member.is_active);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['global-task-members', id] }),
                queryClient.invalidateQueries({ queryKey: ['global-task', id] }),
            ]);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update tenant');
        } finally {
            setTogglingTenantId(null);
        }
    };

    const requestRunAll = (force = false) => {
        if (isLarsTask) {
            setPendingRun({ kind: 'all', force });
            setStageModalOpen(true);
            return;
        }
        void executeRun({ kind: 'all', force });
    };

    const requestRunTenant = (tenantId: string) => {
        if (isLarsTask) {
            setPendingRun({ kind: 'tenant', tenantId });
            setStageModalOpen(true);
            return;
        }
        void executeRun({ kind: 'tenant', tenantId });
    };

    const executeRun = async (run: PendingRun, stages?: number[]) => {
        setError(null);
        setSuccess(null);
        if (run.kind === 'all') {
            setRunningAll(true);
        } else {
            setRunningTenantId(run.tenantId);
        }
        try {
            await apiClient.runGlobalTaskNow(id!, {
                force: run.kind === 'all' ? run.force : true,
                tenantId: run.kind === 'tenant' ? run.tenantId : undefined,
                stages,
            });
            setSuccess(
                run.kind === 'all'
                    ? `Global task run initiated${stages?.length ? ` (stages ${stages.join(', ')})` : ''}. Refresh shortly to see logs.`
                    : `Tenant re-run initiated${stages?.length ? ` (stages ${stages.join(', ')})` : ''}.`,
            );
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['global-task-runs', id] });
                queryClient.invalidateQueries({ queryKey: ['global-task-members', id] });
            }, 3000);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to run task');
        } finally {
            setRunningAll(false);
            setRunningTenantId(null);
            setStageModalOpen(false);
            setPendingRun(null);
        }
    };

    const handleDelete = async () => {
        const response = await apiClient.deleteGlobalTask(id!);
        if (response.success) {
            navigate('/tasks');
        } else {
            setError(response.message || 'Failed to delete');
        }
        setDeleteModalOpen(false);
    };

    const toggleRunExpand = async (run: GlobalTaskRunResponse) => {
        if (expandedRunId === run.id) {
            setExpandedRunId(null);
            return;
        }
        setExpandedRunId(run.id);
        if (!tenantRunsByRun[run.id]) {
            const data = await apiClient.getGlobalTaskTenantRuns(id!, run.id);
            setTenantRunsByRun((prev) => ({ ...prev, [run.id]: data }));
        }
    };

    if (taskLoading || !task) {
        return <div className="p-4">Loading global task...</div>;
    }

    const stageModalTitle =
        pendingRun?.kind === 'tenant'
            ? 'Select stages for tenant re-run'
            : pendingRun?.kind === 'all' && pendingRun.force
              ? 'Select stages for force run'
              : 'Select stages to run';

    return (
        <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
                <div>
                    <button
                        type="button"
                        onClick={() => navigate('/tasks')}
                        className="text-sm text-primary-600 hover:text-primary-800 mb-2"
                    >
                        ← Back to Tasks
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">{formatScheduleName(task.schedule_name)}</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        {task.frequency} · {task.active_member_count ?? 0} active / {task.member_count ?? 0} tenants
                        {isLarsTask && (
                            <> · default stages {defaultStages.join(', ')}</>
                        )}
                    </p>
                </div>
                <div className="flex gap-2 mt-4 sm:mt-0">
                    <button
                        onClick={() => requestRunAll(false)}
                        disabled={runningAll}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                        <PlayIcon className="h-4 w-4 mr-2" />
                        Run All Now
                    </button>
                    <button
                        onClick={() => requestRunAll(true)}
                        disabled={runningAll}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        Force Run All
                    </button>
                    <button
                        onClick={() => setDeleteModalOpen(true)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
            {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{success}</div>}

            <h2 className="text-lg font-semibold mb-3">Tenants</h2>
            <p className="text-sm text-gray-600 mb-3">Uncheck to deactivate a tenant from scheduled runs. Re-check to reactivate.</p>
            <div className="overflow-x-auto mb-8">
                <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Started</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Ended</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {membersLoading ? (
                            <tr><td colSpan={6} className="px-4 py-4 text-sm text-gray-500">Loading...</td></tr>
                        ) : members.map((member) => (
                            <tr key={member.id}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2 min-h-[1.25rem]">
                                        {togglingTenantId === member.tenant_id ? (
                                            <div
                                                className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"
                                                title="Updating…"
                                                aria-label="Updating tenant"
                                            />
                                        ) : (
                                            <input
                                                type="checkbox"
                                                checked={member.is_active}
                                                onChange={() => handleToggleMember(member)}
                                                disabled={!!togglingTenantId}
                                                title={member.is_active ? 'Deactivate' : 'Reactivate'}
                                            />
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{member.tenant_name}</td>
                                <td className="px-4 py-3">{statusBadge(member.period_status)}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                    {member.last_started_at ? new Date(member.last_started_at).toLocaleString() : '—'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                    {member.last_ended_at ? new Date(member.last_ended_at).toLocaleString() : '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        onClick={() => requestRunTenant(member.tenant_id)}
                                        disabled={runningTenantId === member.tenant_id || !member.is_active}
                                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                        title="Re-run this tenant"
                                    >
                                        {runningTenantId === member.tenant_id ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600 inline-block" />
                                        ) : (
                                            <PlayIcon className="h-5 w-5 inline" />
                                        )}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h2 className="text-lg font-semibold mb-3">Run History</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="w-8 px-2 py-3" />
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ended</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {runsLoading ? (
                            <tr><td colSpan={6} className="px-4 py-4 text-sm text-gray-500">Loading...</td></tr>
                        ) : runs.length === 0 ? (
                            <tr><td colSpan={6} className="px-4 py-4 text-sm text-gray-500 text-center">No runs yet.</td></tr>
                        ) : runs.map((run) => (
                            <Fragment key={run.id}>
                                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleRunExpand(run)}>
                                    <td className="px-2 py-3">
                                        {expandedRunId === run.id ? (
                                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                        ) : (
                                            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm">{new Date(run.started_at).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm">{run.ended_at ? new Date(run.ended_at).toLocaleString() : '—'}</td>
                                    <td className="px-4 py-3">{statusBadge(run.status)}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{run.period_key}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{run.trigger}</td>
                                </tr>
                                {expandedRunId === run.id && (
                                    <tr key={`${run.id}-detail`}>
                                        <td colSpan={6} className="px-6 py-4 bg-gray-50">
                                            <table className="min-w-full text-sm">
                                                <thead>
                                                    <tr className="text-left text-xs text-gray-500 uppercase">
                                                        <th className="pb-2 pr-4">Tenant</th>
                                                        <th className="pb-2 pr-4">Started</th>
                                                        <th className="pb-2 pr-4">Ended</th>
                                                        <th className="pb-2 pr-4">Status</th>
                                                        <th className="pb-2">Error / Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(tenantRunsByRun[run.id] || []).map((tr) => (
                                                        <tr key={tr.id} className="border-t border-gray-200 align-top">
                                                            <td className="py-2 pr-4">{tr.tenant_name}</td>
                                                            <td className="py-2 pr-4">{tr.started_at ? new Date(tr.started_at).toLocaleString() : '—'}</td>
                                                            <td className="py-2 pr-4">{tr.ended_at ? new Date(tr.ended_at).toLocaleString() : '—'}</td>
                                                            <td className="py-2 pr-4">{statusBadge(tr.status)}</td>
                                                            <td className="py-2 max-w-xl">
                                                                {tr.error_message ? (
                                                                    <p className="text-red-600 whitespace-pre-wrap break-words">
                                                                        {tr.error_message}
                                                                    </p>
                                                                ) : (
                                                                    <span className="text-gray-400">—</span>
                                                                )}
                                                                {tr.result_json && (
                                                                    <details className="mt-1">
                                                                        <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                                                                            View result details
                                                                        </summary>
                                                                        <pre className="mt-1 text-xs bg-white border rounded p-2 overflow-auto max-h-48 text-gray-700">
                                                                            {JSON.stringify(tr.result_json, null, 2)}
                                                                        </pre>
                                                                    </details>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {!(tenantRunsByRun[run.id]?.length) && (
                                                        <tr><td colSpan={5} className="py-2 text-gray-500">Loading tenant runs...</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Global Task"
                message="Are you sure you want to delete this global task? All run history will be removed."
                confirmText="Delete"
                type="danger"
            />

            <RunLarsStagesModal
                isOpen={stageModalOpen}
                title={stageModalTitle}
                defaultStages={defaultStages}
                isSubmitting={runningAll || !!runningTenantId}
                onClose={() => {
                    if (runningAll || runningTenantId) return;
                    setStageModalOpen(false);
                    setPendingRun(null);
                }}
                onConfirm={(stages) => {
                    if (!pendingRun) return;
                    void executeRun(pendingRun, stages);
                }}
            />
        </div>
    );
}
